import { useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import api from 'services/api'
import {
  causeColor,
  causeHelp,
  causeLabel,
  fmtCount,
  fmtCoverage,
  fmtSeconds,
  coverageTone,
} from './vocabulary'

// The rows under the page's user filter.
//
// Every figure above is an aggregate, and narrowing one to a single account
// answers *how is this person doing* — it cannot answer *what did this phone
// actually send*, which is a question about individual spans: this one still
// owes 3 of 31 segments, that one closed on `jetsam`, this one has been
// declared for nine hours and the device can no longer finish it.
//
// Loaded on demand rather than with the page: it is the third request and it
// is the one call here that is not cached server-side. The account is the
// signed-in staff member's own — the server resolves `me` — until the page's
// filter names someone else, so the panel is never empty for want of a pick.
const PAGE = 50

// Both clocks, side by side, because the gap between them is the subject.
// `start_ms` is the DEVICE's clock — when the words were said — and
// `declared_at` is this server's, stamped when the manifest arrived. A large
// difference is audio that sat on a handset, and no other surface plots it.
const COLUMNS = [
  { key: 'recorded', label: 'Recorded (device)', align: 'left' },
  { key: 'audio_s', label: 'Audio held', align: 'right' },
  { key: 'coverage', label: 'Coverage', align: 'right' },
  { key: 'segments', label: 'Segments', align: 'right' },
  { key: 'released', label: 'Released', align: 'right' },
  { key: 'missing', label: 'Missing', align: 'right' },
  { key: 'state', label: 'State', align: 'left' },
  { key: 'seal_cause', label: 'Closed on', align: 'left' },
  { key: 'declared', label: 'Declared → durable', align: 'right' },
  { key: 'age', label: 'Age', align: 'right' },
]

const STATE_TONE = {
  durable: 'success.main',
  processed: 'success.main',
  declared: 'text.secondary',
  abandoned: 'error.main',
}

const SpanTable = ({ user, coverageBar }) => {
  const [rows, setRows] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const userId = user?.id || 'me'

  const load = useCallback(
    async (beforeStartMs) => {
      setLoading(true)
      try {
        const data = await api.streamMetrics.getSpans({
          userId,
          limit: PAGE,
          beforeStartMs,
        })
        // Append on a page, replace on a fresh load — the cursor is the last
        // row's own key, so the two never overlap.
        setRows((prev) =>
          beforeStartMs == null ? data.rows : [...prev, ...data.rows]
        )
        setCursor(data.next_before_start_ms)
        setError(null)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    setRows([])
    setCursor(null)
    load(null)
  }, [load])

  if (error) {
    return (
      <Typography variant="body2" color="error.main">
        {error}
      </Typography>
    )
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {loading
          ? 'Loading…'
          : user
          ? 'This account has declared no spans.'
          : 'You have declared no spans yet. There is no fleet-wide span list — the figures above are the fleet’s answer.'}
      </Typography>
    )
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map(({ key, label, align }) => (
                <TableCell
                  key={key}
                  align={align}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.start_ms}
                sx={{
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip
                    title={`start_ms ${row.start_ms}${
                      row.device_id ? ` · device ${row.device_id}` : ''
                    }`}
                    placement="right"
                  >
                    <span>
                      {dayjs(row.start_ms).format('MMM D HH:mm')}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.75 }}
                      >
                        {fmtSeconds(row.wall_s)}
                      </Typography>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={`${fmtSeconds(row.declared_s)} declared`}
                    placement="left"
                  >
                    <span>
                      {fmtSeconds(row.audio_s)}
                      {row.declared_s !== row.audio_s && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {' / '}
                          {fmtSeconds(row.declared_s)}
                        </Typography>
                      )}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: coverageTone(row.coverage, coverageBar) }}
                >
                  <Tooltip
                    title={`Device claimed ${fmtCoverage(
                      row.continuity
                    )} of the wall time · ${
                      row.gaps
                        ? `${row.gaps} gap(s), ${fmtSeconds(
                            row.gap_s
                          )} not recorded`
                        : 'no gaps inside this span'
                    }`}
                    placement="left"
                  >
                    <span>{fmtCoverage(row.coverage)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  {fmtCount(row.received)}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    {' / '}
                    {fmtCount(row.segments)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {row.released ? (
                    <Tooltip
                      title="The phone couldn’t produce these and let them go, so the span finished on what arrived."
                      placement="top"
                    >
                      <span>{fmtCount(row.released)}</span>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: row.missing ? 'error.main' : undefined }}
                >
                  {fmtCount(row.missing)}
                </TableCell>
                <TableCell sx={{ color: STATE_TONE[row.state] }}>
                  <Tooltip title={row.reason || ''} placement="top">
                    <span>{row.state}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.seal_cause ? (
                    <Tooltip title={causeHelp(row.seal_cause)} placement="top">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: causeColor(row.seal_cause),
                          }}
                        />
                        {causeLabel(row.seal_cause)}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip
                    title={`declared ${row.declared_at}${
                      row.sealed_at ? ` · durable ${row.sealed_at}` : ''
                    }${row.sealed_by ? ` (${row.sealed_by})` : ''}`}
                    placement="left"
                  >
                    <span>
                      {row.sealed_at
                        ? fmtSeconds(
                            (Date.parse(row.sealed_at) -
                              Date.parse(row.declared_at)) /
                              1000
                          )
                        : '—'}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{fmtSeconds(row.age_s)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={!cursor || loading}
          onClick={() => load(cursor)}
        >
          {cursor ? 'Load older' : 'No older spans'}
        </Button>
        {loading && <CircularProgress size={16} />}
        <Typography variant="caption" color="text.secondary">
          {fmtCount(rows.length)} span{rows.length === 1 ? '' : 's'}, newest
          first
        </Typography>
      </Box>
    </Box>
  )
}

export default SpanTable
